<template>
  <div
    class="space-y-4"
    :class="{ 'min-h-0 overflow-y-auto': !pageScroll && !showFetchNext }"
    ref="scrollPane"
    @scroll="() => !pageScroll && handleScroll()"
  >
    <slot
      name="composer"
      v-bind="{
        feedType,
        feedId,
        parentId: 'NULL',
        onComment: (c: CommentEntity) => updateCommentsList(c),
        onCancel: () => {},
      }"
    >
      <FeedCommentAddForm
        :user="userEntity"
        :feed-id="feedId"
        :feed-type="feedType"
        sync
        @comment="updateCommentsList"
      />
    </slot>
    <FeedCommentCard
      v-for="comment in data ?? []"
      :key="comment.id"
      :comment="comment"
      :feed-type
      :feed-id
      :highlightAuthorIds
      :highlightAuthorTag
      :collapseReplies
    >
      <template #composer="composerAttrs">
        <slot
          name="composer"
          v-bind="{
            ...composerAttrs,
            onComment: (c: CommentEntity) => (
              updateCommentsList(c), composerAttrs.onComment()
            ),
          }"
        >
          <FeedCommentAddForm
            :class="composerAttrs.classAttr"
            :user="userEntity"
            :feed-id="feedId"
            :feed-type="feedType"
            :parent-id="composerAttrs.parentId"
            sync
            @comment="
              (c: CommentEntity) => (
                updateCommentsList(c), composerAttrs.onComment()
              )
            "
          >
            <template
              v-if="composerAttrs.parentId !== 'NULL'"
              #additionalActions="{ isPerformingAction: shouldDisable }"
            >
              <div class="mr-2">
                <NamiButton
                  buttonType="secondary"
                  small
                  pill
                  @buttonClick="composerAttrs.onCancel"
                  :disabled="shouldDisable"
                >
                  <span class="px-1">{{
                    $t("components.feedCommentContent.buttonCancelText")
                  }}</span>
                </NamiButton>
              </div>
            </template>
          </FeedCommentAddForm>
        </slot>
      </template>
      <template #options>
        <!-- Comment actions -->
      </template>
    </FeedCommentCard>
    <NamiLoading v-if="isFetchingNextPage || isLoading" size="small" />
    <NamiButton
      v-else-if="showFetchNext && hasNextPage"
      buttonType="primary"
      text
      block
      @button-click="fetchNextPage"
      >Show more</NamiButton
    >
    <div v-else-if="!data?.length" class="text-center p-4">
      Be the first to comment!
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Comment,
  type CommentableResource,
  type CommentEntity,
  type Collection,
  isComment,
} from "~/src/api";
import { useInfiniteQuery, useQueryClient } from "@tanstack/vue-query";

interface Props {
  feedType: CommentableResource;
  feedId: string;
  highlightAuthorIds?: string[];
  highlightAuthorTag?: string;
  noComments?: boolean;
  hideInput?: boolean;
  disableReplies?: boolean;
  showFetchNext?: boolean;
  // default replies to collapsed
  collapseReplies?: boolean;
  // use window scroll pos
  pageScroll?: boolean;
  // disable the query manager
  disableFetch?: boolean;
  // disable pagination and limit initial to top 3 comments
  preview?: boolean;
}

const { feedType, feedId, pageScroll, showFetchNext, disableFetch, preview } =
  defineProps<Props>();
const emit = defineEmits<{
  (e: "comment", v: CommentEntity): void;
}>();

const authStore = useAuthStore();
const userEntity = computed(() => authStore.userEntity ?? undefined);

const queryClient = useQueryClient();

const feedKey = [
  computed(() => feedType),
  computed(() => feedId),
  { preview: computed(() => !!preview) },
];
const parentCommentsQueryKey = ["comments", ...feedKey];

const { data, fetchNextPage, isFetchingNextPage, hasNextPage, isLoading } =
  useInfiniteQuery({
    queryKey: parentCommentsQueryKey,
    queryFn: async ({ pageParam = 0 }) =>
      Comment.search(feedType, feedId, {
        parentId: "NULL",
        offset: pageParam,
        limit: preview ? 3 : 10,
        order: preview
          ? {
              order: "desc",
            }
          : {
              createdAt: "desc",
            },
        includes: ["user", "profile_frame", "organization", "badge"],
        showDeleted: false,
      }),
    initialPageParam: 0,
    getNextPageParam: (prev, pages) => {
      if (preview) return null;
      if (prev && prev.meta.offset + prev.meta.limit >= prev.meta.total)
        return null;
      return prev.meta.offset + prev.meta.limit;
    },
    select: (data) => data.pages.flatMap((page) => page.data),
    enabled: computed(() => import.meta.server || !disableFetch),
    staleTime: 1000 * 60 * 5,
  });

function updateCommentsList(comment: CommentEntity) {
  emit("comment", comment);
  const parentComment = comment.relationships.find(isComment);

  let keyToUse = parentCommentsQueryKey;
  if (parentComment) {
    keyToUse = ["comments", "replies", computed(() => parentComment.id)];
  }

  queryClient.setQueryData(
    keyToUse,
    (
      old:
        | undefined
        | {
            pages: Collection<CommentEntity>[];
            pageParams: number[];
          },
    ) => {
      // add a new cached result for this entry
      if (!old)
        return {
          pages: [
            {
              type: "collection",
              data: [comment],
              meta: {
                total: 1,
                offset: 0,
                limit: 3,
              },
            },
          ] as Collection<CommentEntity>[],
          pageParams: [0],
        };

      // otherwise, update existing
      const { pages, pageParams } = old;

      const firstPageData = JSON.parse(JSON.stringify(pages[0]));
      if (firstPageData) {
        firstPageData.data.unshift(comment);
        firstPageData.meta.total += 1;
        firstPageData.meta.limit += 1;
      }

      const updatedPageTotals = pages.map((page) => {
        return {
          ...page,
          meta: {
            ...page.meta,
            total: page.meta.total + 1,
            offset: page.meta.offset + 1,
          },
        };
      });

      updatedPageTotals.splice(0, 1, firstPageData);

      return {
        pages: updatedPageTotals,
        pageParams: pageParams.map((param) => (param > 0 ? param + 1 : param)),
      };
    },
  );
}

const scrollPane = ref<HTMLDivElement>();

function handleScroll() {
  if (!scrollPane.value || !hasNextPage.value || showFetchNext) return;
  let shouldFetchNext = false;

  if (pageScroll) {
    const paneRect = scrollPane.value.getBoundingClientRect();
    const bottomDistance = paneRect.height + paneRect.top - window.innerHeight;
    shouldFetchNext = bottomDistance < 100;
  } else {
    const { clientHeight, scrollHeight, scrollTop } = scrollPane.value;
    shouldFetchNext = scrollHeight - clientHeight - scrollTop < 100;
  }

  if (shouldFetchNext && !isFetchingNextPage.value) {
    fetchNextPage();
  }
}

onMounted(() => {
  if (pageScroll) window.addEventListener("scroll", handleScroll);
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped lang="postcss"></style>
